import { Component, OnChanges, ChangeDetectionStrategy, input } from '@angular/core';
import { UnitRatingResponse } from '../../../model/unit-rating-response';
import { UnitConversionUtils } from '../../unit-conversion-utils';
import { CoilResponse } from '../../../model/coil-response';

@Component({
  selector: 'app-performance-results',
  templateUrl: './performance-results.component.html',
  styleUrls: ['./performance-results.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class PerformanceResultsComponent implements OnChanges {
  readonly isMetric = input(false);
  readonly ratingResponse = input.required<UnitRatingResponse>();
  readonly isSureflow = input<boolean>(undefined!);
  coolingTotalCapacity = 0;
  coolingSensibleCapacity = 0;
  coolingLeavingDryBulb = 0;
  coolingLeavingWetBulb = 0;
  coolingFluidFlowRate = 0;
  coolingFluidTemperatureChange = 0;
  coolingLeavingFluidTemperature = 0;
  coolingFluidPressureDrop = 0;
  heatingTotalCapacity = 0;
  heatingLeavingDryBulb = 0;
  heatingFluidFlowRate = 0;
  heatingFluidTemperatureChange = 0;
  heatingLeavingFluidTemperature = 0;
  heatingFluidPressureDrop = 0;
  electricHeatLeavingDryBulb = 0;

  ngOnChanges(): void {
    this.clearOutputValues();
    if (this.ratingResponse()) {
      const primaryCoil = this.ratingResponse().PrimaryCoilResponse;
      if (primaryCoil) {
        if (this.ratingResponse().primaryCooling) {
          this.setCoolingProperties(primaryCoil);
        } else {
          this.setHeatingProperties(primaryCoil);
        }
      }
      const secondaryCoil = this.ratingResponse().SecondaryCoilResponse;
      if (secondaryCoil) {
        this.setHeatingProperties(secondaryCoil);
      }
      const electricHeat = this.ratingResponse().ElectricHeatResponse;
      if (electricHeat) {
        this.electricHeatLeavingDryBulb = electricHeat.LeavingDryBulbTemperature;
      }
    }
  }

  private setHeatingProperties(coilResponse: CoilResponse): void {
    this.heatingTotalCapacity = coilResponse.TotalCapacity;
    this.heatingLeavingDryBulb = coilResponse.LeavingDryBulbTemperature;
    this.heatingFluidFlowRate = coilResponse.FluidFlowRate;
    this.heatingFluidTemperatureChange = coilResponse.FluidTemperatureChange;
    this.heatingLeavingFluidTemperature = coilResponse.LeavingFluidTemperature;
    this.heatingFluidPressureDrop = coilResponse.FluidPressureDrop;
  }

  private setCoolingProperties(coilResponse: CoilResponse): void {
    this.coolingTotalCapacity = coilResponse.TotalCapacity;
    this.coolingSensibleCapacity = coilResponse.SensibleCapacity;
    this.coolingLeavingDryBulb = coilResponse.LeavingDryBulbTemperature;
    this.coolingLeavingWetBulb = coilResponse.LeavingWetBulbTemperature;
    this.coolingFluidFlowRate = coilResponse.FluidFlowRate;
    this.coolingFluidTemperatureChange = coilResponse.FluidTemperatureChange;
    this.coolingLeavingFluidTemperature = coilResponse.LeavingFluidTemperature;
    this.coolingFluidPressureDrop = coilResponse.FluidPressureDrop;
  }

  private clearOutputValues(): void {
    this.coolingTotalCapacity = 0;
    this.coolingSensibleCapacity = 0;
    this.coolingLeavingDryBulb = 0;
    this.coolingLeavingWetBulb = 0;
    this.coolingFluidFlowRate = 0;
    this.coolingFluidTemperatureChange = 0;
    this.coolingLeavingFluidTemperature = 0;
    this.coolingFluidPressureDrop = 0;
    this.heatingTotalCapacity = 0;
    this.heatingLeavingDryBulb = 0;
    this.heatingFluidFlowRate = 0;
    this.heatingFluidTemperatureChange = 0;
    this.heatingLeavingFluidTemperature = 0;
    this.heatingFluidPressureDrop = 0;
    this.electricHeatLeavingDryBulb = 0;
  }

  getCapacityUnits(): string {
    return UnitConversionUtils.getCapacityUnits(this.isMetric());
  }

  getTemperatureUnits(): string {
    return UnitConversionUtils.getTemperatureUnits(this.isMetric());
  }

  getFluidFlowUnits(): string {
    return UnitConversionUtils.getFlowRateUnits(this.isMetric());
  }

  getPressureDropUnits(): string {
    return UnitConversionUtils.getPressureUnits(this.isMetric());
  }
}
